import ApiClient from './apiClient';
import { Contact } from '../components/common/ContactForm';

export interface ContactFormData {
  nickname: string;
  contactName: string;
  companyName: string;
  email: string;
  phone: string;
  street: string;
  houseNo: string;
  zipCode: string;
  deliveryDetails: string;
  isFavorite: boolean;
  isDefault?: boolean;
  
  // New fields for API-based location data
  country_id?: number | null;
  state_id?: number | null;
  city_id?: number | null;
  city_other?: string;
}

// Define the paginated response interface
export interface PaginatedResponse<T> {
  data: T[];
  status?: string;
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: any[];
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

// Define API response for single contact
interface ContactResponse {
  status: string;
  data: Contact;
}

const getContacts = async (params?: {
  search?: string;
  default?: boolean;
  page?: number;
  per_page?: number;
  country_id?: number;
}): Promise<PaginatedResponse<Contact>> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return {
      status: "success",
      data: [],
      links: { first: "", last: "", prev: null, next: null },
      meta: {
          current_page: 1,
          from: 0,
          last_page: 1,
          links: [],
          path: "",
          per_page: 15,
          to: 0,
          total: 0
        }
    };
  }
  
  return ApiClient.get<PaginatedResponse<Contact>>('/contacts', params);
};

const searchContacts = async (query: string): Promise<PaginatedResponse<Contact>> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return {
      status: "success",
      data: [],
      links: { first: "", last: "", prev: null, next: null },
      meta: {
          current_page: 1,
          from: 0,
          last_page: 1,
          links: [],
          path: "",
          per_page: 15,
          to: 0,
          total: 0
        }
    };
  }
  
  return ApiClient.get<PaginatedResponse<Contact>>('/contacts', { search: query });
};

const getContact = async (id: string): Promise<Contact> => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await ApiClient.get<ContactResponse>(`/contacts/${id}`);
  return response.data;
};

const addContact = async (contactData: Partial<Contact>): Promise<Contact> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Authentication required');
  }
  
  const response = await ApiClient.post<ContactResponse, Partial<Contact>>('/contacts', contactData);
  return response.data;
};

const updateContact = async ({ id, data }: { id: string, data: Partial<Contact> }): Promise<Contact> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Authentication required');
  }
  
  const response = await ApiClient.put<ContactResponse, Partial<Contact>>(`/contacts/${id}`, data);
  return response.data;
};

const deleteContact = async (id: string): Promise<void> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Authentication required');
  }
  
  await ApiClient.delete(`/contacts/${id}`);
};

const toggleFavorite = async (id: string): Promise<void> => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('Authentication required');
  }

  await ApiClient.put(`/contacts/${id}/toggle-favorite`, {});
};

const setAsDefault = async (id: string): Promise<void> => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('Authentication required');
  }

  await ApiClient.put(`/contacts/${id}/set-as-default`, {});
};

const contactsService = {
  getContacts,
  getContact,
  addContact,
  updateContact,
  deleteContact,
  toggleFavorite,
  setAsDefault,
  searchContacts
};

export default contactsService; 
